import handleErrors from '@/functions/handleErrors';
import typeOf from '@/functions/findTypeOfValue';
import { cloneDeep, findIndex } from 'lodash';

const state = {
  messages: [],
};

//what types of messages we can have. it affects the color of the message and the functionality (e.g. 'error' contains error object)
const AVAILABLE_TYPES_OF_MESSAGES = ['success', 'error', 'warning', 'info'];

//in what programming type the message value is provided
const AVAILABLE_DATA_TYPES = ['string', 'error', 'array'];

// the max number of messages that the store will hold. when exceeded, the first/earliest message will be deleted
const MAXIMUM_NUMBER_OF_MESSAGES = 10;

//the default value for when passing the parameter to reset messages, it will reset all messages
const DEFAULT_RESET_MESSAGES_TYPE = 'all';

const getters = {
  messages: state => {
    return state.messages;
  },
  //modular function to return messages of given type (e.g. 'error', 'success' etc.)
  messagesWithType: state => (type) => {
    return state.messages.filter(message => message.type === type);
  },
  messagesWithDisplayType: state => (displayType) => {
    return state.messages.filter(message => message.displayType === displayType);
  },
  messagesWithName: state => (name) => {
    return state.messages.filter(message => message.name === name);
  },
  messagesWithNameAndDisplayType: (state, getters) => (name, displayType) => {
    return getters.messagesWithName(name).filter(message => message.displayType === displayType);
  },
  errorMessages: (_state, getters) => {
    return getters.messagesWithType('error');
  },
}


const mutations = {
  setMessages(state, messages) {
    //[{detail:"Success", type: "success"}, {detail:"Failed", type: "error"}]
    state.messages = messages;
  },

  setMessage(state, message) {
    state.messages.push(message);
  },

  resetMessages(state, type = DEFAULT_RESET_MESSAGES_TYPE) {
    if (type == DEFAULT_RESET_MESSAGES_TYPE)
      state.messages = [];

    state.messages = state.messages.filter(function (obj) {
      return obj.type !== type;
    });
  },
  deleteEarliestMessage() {
    state.messages.shift();
  },
  deleteMessageByTimeout(state, messageId) {
    var index = findIndex(
      state.messages,
      x => x.id == messageId
    );
    if (index == -1) return;

    state.messages.splice(index, 1);
  },
};

const actions = {
  /*
    passedMsg = {
      name(String):                 name of the message component where the message is to be shown
      displayType(String):          how we want to display the message ('popup' or 'inline')
      type(String):                 what kind of message it is ('error', 'success' etc). Must be inside the AVAILABLE_TYPES_OF_MESSAGES variable
      message(String/Error object): what message we want to show. string for static message, error object for try/catch(e) error.
                                    Data type of value must be inside the AVAILABLE_DATA_TYPES variable
      (optional)options(Object):    Options for message. can pass timeout for how long we want to hav the message to be shown
    }
  */
  setMessage({ dispatch }, passedMsg = {}) {
    let tempObj = {
      detail: passedMsg.detail || passedMsg.message,
      type: passedMsg.type,
      dataType: typeOf(passedMsg.message),
      displayType: passedMsg.displayType || 'popup',
      name: passedMsg.name || 'layout',
      options: passedMsg.options || {},
    };

    //check that the message value data type is 'error' or 'string'
    if (AVAILABLE_DATA_TYPES.includes(tempObj.dataType)) {
      //automatically set type to 'error' if the message value data type is an Error object
      if (tempObj.dataType === 'error' && !tempObj.type)
        tempObj.type = 'error';
    } else {
      console.error(`Provided message value data type: ${tempObj.dataType}, is not included in the list of available data types. Available data types: `);
      console.log(AVAILABLE_DATA_TYPES);
      return false;
    }

    //type must either be success/error/warning/info
    if (!AVAILABLE_TYPES_OF_MESSAGES.includes(tempObj.type)) {
      console.error(`Provided message type: ${tempObj.type}, is not included in the list of available types of messages. Available types: `);
      console.log(AVAILABLE_TYPES_OF_MESSAGES);
      return false;
    }

    let tempErrorObj = cloneDeep(tempObj);
    if (tempObj.dataType == 'string') {
      dispatch('prepareMessageToBeStored', tempObj);
    } else if (tempObj.dataType == 'array') {
      for (var i = 0; i < tempObj.detail.length; i += 1) {
        tempErrorObj.detail = tempObj.detail[i].detail;
        tempErrorObj.index = i;

        dispatch('prepareMessageToBeStored', tempErrorObj);
      }
    } else {
      /*
        Handle error objects
        https://stackoverflow.com/a/30469297
      */
      const arrayOfErrors = handleErrors(tempObj.detail);
      arrayOfErrors.forEach((error, i) => {
        tempErrorObj.detail = error.detail || error.message;
        tempErrorObj.index = i;

        dispatch('prepareMessageToBeStored', tempErrorObj);
      });
    }
  },

  /*
     Message to prepare before dispatching it to be commited to the store.
     Passed object must always have the keys: message, type 
  */
  prepareMessageToBeStored({ dispatch }, messageToPrepare) {
    let msgObj = cloneDeep(messageToPrepare);
    msgObj.id = "id" + Math.random().toString(16).slice(2);

    //finding and assigning default values for options
    const providedOptions = messageToPrepare?.options;
    var timeout = providedOptions?.timeout || -1;

    //build final options object 
    const options = { timeout: timeout };

    msgObj.options = options;

    //dispatch for final handling logic before commiting to the store. 
    //also pass index of error message(to know if we need to delete previous messages)
    dispatch('saveMessage', msgObj);
  },

  /*setter to commit message to the store
    contains logic on how to handle messages depending of their type */
  saveMessage({ dispatch, commit }, message) {
    /*if the type of message is "success"
        -> all the messages before the current one was inserted, are deleted */
    if (message.type == "success") {
      dispatch('resetMessages');

      /*if the type of message is "error"
        -> all the "success" type messages in the messages queue are deleted */
    } else if (message.type == "error") {
      dispatch('resetSuccessMessages');
      //and if its the first error message (error object can have multiple messages), then remove old errors from messages list
      if (message.index == 0)
        dispatch('resetErrorMessages');
    }

    commit('setMessage', message);

    if (message.options.timeout != -1) {
      //delete dispatch message
      dispatch('deleteTimeoutMessage', message);
    }
    // Maximum number of messages allowed to be stored in the messages queue
    if (state.messages.length > MAXIMUM_NUMBER_OF_MESSAGES) {
      commit('deleteEarliestMessage');
    }
  },
  deleteTimeoutMessage({ commit }, fullObj) {
    setTimeout(() => {
      commit('deleteMessageByTimeout', fullObj.id);
    }, fullObj.options.timeout);
  },

  //DEVNOTE: Due to changes to allow messages to go to specific Messages components, functionality
  // that affects all/subset of messages should be changed so that it only affects messages only for their own messages components

  //modular function for resetting given types of messages.
  resetMessages({ commit }, type = DEFAULT_RESET_MESSAGES_TYPE) {
    commit('resetMessages', type);
  },
  resetErrorMessages({ dispatch }) {
    dispatch('resetMessages', 'error');
  },
  resetSuccessMessages({ dispatch }) {
    dispatch('resetMessages', 'success');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
